import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPlans, getMyAccessStatus, createCheckoutSession } from "@/api/subscriptions";
import { useAuthStore } from "@/stores/authStore";
import type { PlanType } from "@/types";

const PLAN_ORDER: PlanType[] = ["one_week", "two_weeks", "three_months", "monthly"];

export default function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled") === "1";
  const { isAuthenticated } = useAuthStore();
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: getPlans,
  });

  const { data: accessStatus } = useQuery({
    queryKey: ["access-status"],
    queryFn: getMyAccessStatus,
    enabled: isAuthenticated,
  });

  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const checkoutMutation = useMutation({
    mutationFn: (planType: PlanType) => createCheckoutSession(planType),
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
    onError: (error: any) => {
      const detail = (error?.data as any)?.detail;
      const message = detail || error?.message || "Failed to start checkout. Please try again.";
      setCheckoutError(message);
    },
  });

  const handleSelectPlan = (planType: PlanType) => {
    if (!isAuthenticated) {
      navigate("/login?returnTo=/pricing");
      return;
    }
    setSelectedPlan(planType);
    checkoutMutation.mutate(planType);
  };

  const handleFreeTrial = () => {
    if (!isAuthenticated) {
      navigate("/register");
      return;
    }
    navigate("/free-trial");
  };

  const sortedPlans = [...plans].sort(
    (a, b) => PLAN_ORDER.indexOf(a.type) - PLAN_ORDER.indexOf(b.type)
  );

  const hasUsedTrial = accessStatus?.has_used_free_trial ?? false;
  const hasSubscription = accessStatus?.has_active_subscription ?? false;

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Get Full Access</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose a plan and start practising for your UK driving theory test. All plans
            include full access to all mock exams, categories, and traffic signs quizzes.
          </p>
          {cancelled && (
            <div className="mt-4 inline-block bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg px-4 py-2 text-sm">
              Payment was cancelled. No charge was made.
            </div>
          )}
          {checkoutError && (
            <div className="mt-4 inline-block bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-2 text-sm">
              {checkoutError}
            </div>
          )}
          {hasSubscription && (
            <div className="mt-4 inline-block bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">
              You have an active subscription — you already have full access.
            </div>
          )}
        </div>

        {/* Free Trial Banner */}
        {!hasUsedTrial && !hasSubscription && (
          <div className="mb-10 bg-blue-600 rounded-2xl p-6 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold uppercase tracking-wide opacity-80 mb-1">
                Start for free
              </div>
              <h2 className="text-2xl font-bold mb-1">Try a Free Mock Exam</h2>
              <p className="opacity-90 text-sm">
                50 questions · 57 minutes · No card required
              </p>
            </div>
            <button
              onClick={handleFreeTrial}
              className="shrink-0 bg-white text-blue-700 font-semibold px-8 py-3 rounded-xl hover:bg-blue-50 transition"
            >
              Start Free Trial
            </button>
          </div>
        )}

        {hasUsedTrial && !hasSubscription && (
          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-4 text-center text-amber-800 text-sm">
            You've used your free trial. Subscribe below to continue practising.
          </div>
        )}

        {/* Plans grid */}
        {plansLoading ? (
          <div className="text-center text-gray-500 py-12">Loading plans…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sortedPlans.map((plan) => {
              const isMonthly = plan.is_subscription;
              const isLoading = checkoutMutation.isPending && selectedPlan === plan.type;

              return (
                <div
                  key={plan.type}
                  className={`relative bg-white rounded-2xl border-2 p-6 flex flex-col shadow-sm transition hover:shadow-md ${
                    isMonthly
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-gray-200"
                  }`}
                >
                  {isMonthly && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                  </div>

                  <div className="mb-6">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {plan.price_display}
                    </span>
                    {isMonthly && (
                      <span className="text-gray-500 text-sm ml-1">/month</span>
                    )}
                  </div>

                  <ul className="text-sm text-gray-600 space-y-2 mb-8 flex-1">
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> All mock exams
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> Category practice
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span> Traffic signs quiz
                    </li>
                    {isMonthly ? (
                      <li className="flex items-center gap-2">
                        <span className="text-blue-500">↻</span> Auto-renews monthly
                      </li>
                    ) : (
                      <li className="flex items-center gap-2">
                        <span className="text-green-500">✓</span> One-time payment
                      </li>
                    )}
                  </ul>

                  <button
                    onClick={() => handleSelectPlan(plan.type)}
                    disabled={checkoutMutation.isPending || hasSubscription}
                    className={`w-full py-3 rounded-xl font-semibold text-sm transition disabled:opacity-60 ${
                      isMonthly
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-900 text-white hover:bg-gray-700"
                    }`}
                  >
                    {isLoading ? "Redirecting…" : hasSubscription ? "Already subscribed" : "Get Started"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-10">
          Payments are processed securely by Stripe. Cancel anytime from your profile.
        </p>
      </div>
    </div>
  );
}
